import type { PrevTailData } from "@/hooks/roster-craete/useRosterPrevTail";
import { NURSE_LIST_COLUMN_WIDTHS } from "@/components/feature/rosterTable/script";

export const ROSTER_GRID_GAP_PX = 14;
export const ROSTER_DAY_CELL_PX = 34;
export const ROSTER_SUMMARY_DATE_LABEL_PX = 80;
export const ROSTER_PREV_TAIL_HEADER_PX = 205;

export type RosterTableLayout = "create" | "compact";

export const ROSTER_BODY_FIRST_COL_PX =
  NURSE_LIST_COLUMN_WIDTHS.handle +
  NURSE_LIST_COLUMN_WIDTHS.name +
  NURSE_LIST_COLUMN_WIDTHS.preceptor +
  NURSE_LIST_COLUMN_WIDTHS.grade +
  NURSE_LIST_COLUMN_WIDTHS.role +
  NURSE_LIST_COLUMN_WIDTHS.level +
  NURSE_LIST_COLUMN_WIDTHS.experience;
export const ROSTER_COMPACT_BODY_FIRST_COL_PX =
  ROSTER_BODY_FIRST_COL_PX - NURSE_LIST_COLUMN_WIDTHS.level;
export const ROSTER_SUMMARY_FIRST_COL_PX =
  ROSTER_BODY_FIRST_COL_PX - ROSTER_SUMMARY_DATE_LABEL_PX;
export const ROSTER_COMPACT_SUMMARY_FIRST_COL_PX =
  ROSTER_COMPACT_BODY_FIRST_COL_PX - ROSTER_SUMMARY_DATE_LABEL_PX;

export type RosterTableLayoutConfig = {
  readonly bodyFirstColPx: number;
  readonly summaryFirstColPx: number;
  readonly summaryFirstColWithPrevTailPx: number;
  readonly showPreceptorColumn: boolean;
  readonly showLevelColumn: boolean;
  readonly showRowReorder: boolean;
  readonly showGradeColumn?: boolean;
  readonly showExperienceColumn?: boolean;
};

export function buildRosterTableLayoutConfig({
  showPreceptorColumn,
  showLevelColumn,
  showRowReorder = false,
  showGradeColumn = true,
  showExperienceColumn = true,
}: {
  readonly showPreceptorColumn: boolean;
  readonly showLevelColumn: boolean;
  readonly showRowReorder?: boolean;
  readonly showGradeColumn?: boolean;
  readonly showExperienceColumn?: boolean;
}): RosterTableLayoutConfig {
  const bodyFirstColPx =
    NURSE_LIST_COLUMN_WIDTHS.handle +
    NURSE_LIST_COLUMN_WIDTHS.name +
    (showPreceptorColumn ? NURSE_LIST_COLUMN_WIDTHS.preceptor : 0) +
    (showGradeColumn ? NURSE_LIST_COLUMN_WIDTHS.grade : 0) +
    (showLevelColumn
      ? NURSE_LIST_COLUMN_WIDTHS.role + NURSE_LIST_COLUMN_WIDTHS.level
      : 0) +
    (showExperienceColumn ? NURSE_LIST_COLUMN_WIDTHS.experience : 0);

  const summaryFirstColPx = Math.max(
    0,
    bodyFirstColPx - ROSTER_SUMMARY_DATE_LABEL_PX
  );

  return {
    bodyFirstColPx,
    summaryFirstColPx,
    summaryFirstColWithPrevTailPx:
      summaryFirstColPx + ROSTER_PREV_TAIL_HEADER_PX,
    showPreceptorColumn,
    showLevelColumn,
    showRowReorder,
    showGradeColumn,
    showExperienceColumn,
  };
}

export function getRosterBodyFirstColPx(
  layout: RosterTableLayout = "create",
  layoutConfig?: RosterTableLayoutConfig
): number {
  if (layoutConfig) return layoutConfig.bodyFirstColPx;
  return layout === "compact"
    ? ROSTER_COMPACT_BODY_FIRST_COL_PX
    : ROSTER_BODY_FIRST_COL_PX;
}

export function getBodyScheduleDayStripOffsetPx(
  prevTailData: PrevTailData | null | undefined,
  layout: RosterTableLayout = "create",
  layoutConfig?: RosterTableLayoutConfig
): number {
  return (
    getRosterBodyFirstColPx(layout, layoutConfig) +
    ROSTER_GRID_GAP_PX +
    getPrevTailColumnCount(prevTailData) * ROSTER_DAY_CELL_PX
  );
}

export function getPrevTailColumnCount(
  prevTailData: PrevTailData | null | undefined
): number {
  if (!prevTailData?.nurses?.length) return 0;
  const prevNurse = prevTailData.nurses[0];
  const prevDaysSource =
    prevTailData.tail_days && prevTailData.tail_days.length > 0
      ? prevTailData.tail_days
      : prevNurse?.shifts
        ? Object.keys(prevNurse.shifts).map(Number)
        : [];
  const prevDays = prevDaysSource
    .map(Number)
    .filter((day) => day >= 1 && day <= 31)
    .sort((a, b) => a - b);
  return prevDays.length;
}

export function getRosterScheduleDayStripOffsetPx(opts: {
  rosterGridType?: "ROSTER_CREATE" | "ROSTER_ONLY_VIEW";
  hasPrevTail: boolean;
  prevTailColumnCount: number;
  layout?: RosterTableLayout;
  layoutConfig?: RosterTableLayoutConfig;
}): number {
  const {
    rosterGridType,
    hasPrevTail,
    prevTailColumnCount,
    layout,
    layoutConfig,
  } = opts;
  let nurseBlock = getRosterBodyFirstColPx(layout, layoutConfig);
  if (rosterGridType === "ROSTER_CREATE") {
    nurseBlock = layoutConfig
      ? hasPrevTail
        ? layoutConfig.summaryFirstColWithPrevTailPx
        : layoutConfig.summaryFirstColPx
      : hasPrevTail
        ? ROSTER_SUMMARY_FIRST_COL_PX + ROSTER_PREV_TAIL_HEADER_PX
        : ROSTER_SUMMARY_FIRST_COL_PX;
  } else if (rosterGridType === "ROSTER_ONLY_VIEW") {
    nurseBlock = layoutConfig?.summaryFirstColPx ?? ROSTER_SUMMARY_FIRST_COL_PX;
  }
  return (
    nurseBlock + ROSTER_GRID_GAP_PX + prevTailColumnCount * ROSTER_DAY_CELL_PX
  );
}
